import React, { useState, useEffect } from 'react';
import { apiFetch } from '../api';
import './Home.css';

interface Company {
  nom: string;
  siren: string;
  siret: string;
  forme_juridique: string;
  date_creation: string;
  activite: string;
  lieu: string;
  code_postal: string;
  statut: string;
}

interface ScanProgress {
  id: string;
  siret: string;
  companyName: string;
  progress: number;
  score: number | null;
  completed: boolean;
}

interface HomeProps {
  onViewReport: (siret: string) => void;
  onStartScan: (scan: any) => void;
  onUpdateScan: (id: string, updates: any) => void;
  onRemoveScan: (id: string) => void;
  isAuthenticated: boolean;
  onRequestAuth: () => void;
  userEmail: string;
  quotaLeft: number;
}

function Home({ onViewReport, onStartScan, onUpdateScan, onRemoveScan, isAuthenticated, onRequestAuth, userEmail, quotaLeft }: HomeProps) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchResults, setSearchResults] = useState<Company[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [currentQuery, setCurrentQuery] = useState('');
  const [showQuotaTooltip, setShowQuotaTooltip] = useState(false);
  const [tooltipTimeout, setTooltipTimeout] = useState<NodeJS.Timeout | null>(null);

  console.log('Home - isAuthenticated:', isAuthenticated, 'quotaLeft:', quotaLeft, 'userEmail:', userEmail);

  const isSIRET = (value: string) => {
    return /^\d{14}$/.test(value.replace(/\s/g, ''));
  };

  const isSIREN = (value: string) => {
    return /^\d{9}$/.test(value.replace(/\s/g, ''));
  };

  const startScan = async (siret: string, companyName: string = '') => {
    const scanId = Date.now().toString();
    let currentProgress = 0;

    const newScan = {
      id: scanId,
      siret,
      company_name: companyName || siret,
      progress: 0,
      score: null,
      loading: true,
      checked_at: new Date().toISOString(),
      risk_level: 'unknown'
    };

    onStartScan(newScan);

    const progressInterval = setInterval(() => {
      currentProgress = Math.min(90, currentProgress + 1.5);
      onUpdateScan(scanId, {
        progress: currentProgress
      });
    }, 100);

    try {
      const checkPath = userEmail
        ? `/api/solvability/check/${siret}?userEmail=${encodeURIComponent(userEmail)}`
        : `/api/solvability/check/${siret}`;
      const response = await apiFetch(checkPath);
      const result = await response.json();

      clearInterval(progressInterval);

      if (result.success) {
        const finalCompanyName = companyName || result.data.company_info?.company_name || siret;

        onUpdateScan(scanId, {
          company_name: finalCompanyName,
          progress: 100,
          score: result.data.financial_score || 0,
          loading: false,
          risk_level: result.data.risk_level || 'unknown'
        });

        setTimeout(() => {
          onRemoveScan(scanId);
        }, 100);
      } else if (result.error === 'QUOTA_EXCEEDED') {
        clearInterval(progressInterval);
        onRemoveScan(scanId);
        setError(result.message || 'Quota de vérifications épuisé');
      } else {
        clearInterval(progressInterval);
        onRemoveScan(scanId);
        setError(result.message || 'Erreur lors de la vérification');
      }
    } catch (err: any) {
      clearInterval(progressInterval);
      onRemoveScan(scanId);
      setError('Erreur lors de la vérification');
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isAuthenticated) {
      onRequestAuth();
      return;
    }

    if (quotaLeft <= 0) {
      setError('Quota de vérifications épuisé');
      return;
    }

    if (!query.trim()) {
      setError('Veuillez saisir un SIRET ou un nom d\'entreprise');
      return;
    }

    const cleanQuery = query.trim().replace(/\s/g, '');

    if (isSIRET(cleanQuery) || isSIREN(cleanQuery)) {
      setLoading(true);
      try {
        const response = await fetch(`https://recherche-entreprises.api.gouv.fr/search?q=${cleanQuery}`);
        const data = await response.json();

        if (data.results && data.results.length > 0) {
          const company = data.results[0];
          const companyName = company.nom_complet || company.nom_raison_sociale || cleanQuery;
          const siret = company.siege?.siret || (isSIRET(cleanQuery) ? cleanQuery : null);

          if (siret) {
            onViewReport('history');
            setTimeout(() => startScan(siret, companyName), 100);
            setQuery('');
            setError('');
          } else {
            setError('Impossible de trouver le SIRET du siège pour ce SIREN');
          }
        } else {
          setError('Entreprise non trouvée');
        }
      } catch (err) {
        setError('Erreur lors de la recherche');
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setError('');
    setSearchResults([]);
    setShowResults(false);
    setCurrentPage(1);
    setCurrentQuery(query);

    try {
      const response = await apiFetch(`/api/solvability/search?query=${encodeURIComponent(query)}&page=1`);
      const data = await response.json();

      if (data.success) {
        setTotalPages(data.data.total_pages || 1);

        if (data.data.companies.length === 0) {
          setSearchResults([]);
          setShowResults(true);
        } else if (data.data.companies.length === 1 && data.data.total_pages === 1) {
          onViewReport('history');
          setTimeout(() => startScan(data.data.companies[0].siret, data.data.companies[0].nom), 100);
          setQuery('');
        } else {
          setSearchResults(data.data.companies);
          setShowResults(true);
        }
      } else {
        setError(data.message || 'Erreur lors de la recherche');
      }
    } catch (err: any) {
      setError('Erreur de connexion: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreResults = async () => {
    if (currentPage >= totalPages || loading) return;

    setLoading(true);
    const nextPage = currentPage + 1;

    try {
      const response = await apiFetch(`/api/solvability/search?query=${encodeURIComponent(currentQuery)}&page=${nextPage}`);
      const data = await response.json();

      if (data.success && data.data.companies.length > 0) {
        setSearchResults(prev => [...prev, ...data.data.companies]);
        setCurrentPage(nextPage);
      }
    } catch (err: any) {
      setError('Erreur lors du chargement: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleCheckSolvability = (event: any) => {
      const { siret } = event.detail;
      if (siret) {
        startScan(siret, '');
      }
    };

    const handleTriggerSearch = async (event: any) => {
      const { query } = event.detail;
      if (query) {
        setQuery(query);
        setError('');
        setCurrentPage(1);
        setCurrentQuery(query);

        // Afficher immédiatement la zone de résultats avec loading
        setShowResults(true);
        setLoading(true);

        try {
          const response = await apiFetch(`/api/solvability/search?query=${encodeURIComponent(query)}&page=1`);
          const data = await response.json();

          if (data.success && data.data.companies.length > 0) {
            setSearchResults(data.data.companies);
            setTotalPages(data.data.total_pages || 1);
          } else {
            setError('Aucune entreprise trouvée');
            setShowResults(false);
          }
        } catch (err: any) {
          setError('Erreur de connexion: ' + err.message);
          setShowResults(false);
        } finally {
          setLoading(false);
        }
      }
    };

    window.addEventListener('checkSolvability', handleCheckSolvability);
    window.addEventListener('triggerSearch', handleTriggerSearch);
    return () => {
      window.removeEventListener('checkSolvability', handleCheckSolvability);
      window.removeEventListener('triggerSearch', handleTriggerSearch);
    };
  }, []);

  const getSkeletonCount = () => {
    const currentCount = searchResults.length;
    const remainder = currentCount % 3;

    if (remainder === 0) {
      return 3;
    } else {
      return 3 - remainder + 3;
    }
  };

  return (
    <div className={`home ${showResults ? 'home-results-mode' : 'home-search-mode'}`}>
      {!showResults && (
        <div className="home-hero">
          <h1>B2BCheck</h1>
          <p className="home-subtitle">
            Analyse de solvabilité pour entreprises
          </p>

          <form onSubmit={handleSearch} className="home-search-form">
            <div className="search-input-wrapper">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher une entreprise..."
                className="home-search-input"
                disabled={loading}
              />
              <div
                style={{ position: 'relative', display: 'inline-block' }}
                onMouseEnter={() => {
                  console.log('Mouse enter - isAuthenticated:', isAuthenticated, 'quotaLeft:', quotaLeft);
                  if (tooltipTimeout) clearTimeout(tooltipTimeout);
                  if (isAuthenticated && quotaLeft <= 0) {
                    console.log('Setting tooltip to true');
                    setShowQuotaTooltip(true);
                  }
                }}
                onMouseLeave={() => {
                  console.log('Mouse leave - hiding tooltip with delay');
                  const timeout = setTimeout(() => {
                    setShowQuotaTooltip(false);
                  }, 300);
                  setTooltipTimeout(timeout);
                }}
              >
                <button
                  type="submit"
                  className="home-search-btn"
                  disabled={loading || (isAuthenticated && quotaLeft <= 0)}
                >
                  {loading ? (
                    <span className="spinner-small"></span>
                  ) : (
                    <span>Rechercher →</span>
                  )}
                </button>
                {(() => {
                  console.log('Rendering tooltip check - showQuotaTooltip:', showQuotaTooltip, 'isAuthenticated:', isAuthenticated, 'quotaLeft:', quotaLeft);
                  return showQuotaTooltip && isAuthenticated && quotaLeft <= 0;
                })() && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 'calc(100% + 10px)',
                      right: 0,
                      background: 'white',
                      border: '1px solid #000',
                      padding: '16px',
                      minWidth: '320px',
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                      zIndex: 1000
                    }}
                    onMouseEnter={() => {
                      if (tooltipTimeout) clearTimeout(tooltipTimeout);
                      setShowQuotaTooltip(true);
                    }}
                    onMouseLeave={() => {
                      const timeout = setTimeout(() => {
                        setShowQuotaTooltip(false);
                      }, 300);
                      setTooltipTimeout(timeout);
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        border: '2px solid #000',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        flexShrink: 0
                      }}>i</div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: '0.9rem', color: '#000', marginBottom: '12px', fontWeight: '500' }}>
                          Quota de vérifications épuisé
                        </p>
                        <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '12px' }}>
                          Passez à une offre supérieure ou contactez-nous pour une offre sur mesure.
                        </p>
                        <a href="mailto:contact@b2bcheck.fr" style={{
                          display: 'inline-block',
                          padding: '8px 16px',
                          background: '#000',
                          color: '#fff',
                          textDecoration: 'none',
                          fontSize: '0.85rem',
                          borderRadius: '2px'
                        }}>
                          Nous contacter
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <p className="search-hint">
              Recherche possible par SIRET, SIREN, nom d'entreprise, nom et prénom du dirigeant
            </p>

            {error && <div className="home-error">{error}</div>}
          </form>


          <div className="home-features">
          <div className="feature">
            <svg className="feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3v18h18" />
              <path d="M18 17V9" />
              <path d="M13 17V5" />
              <path d="M8 17v-3" />
            </svg>
            <span className="feature-text">Score de solvabilité</span>
          </div>
          <div className="feature">
            <svg className="feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span className="feature-text">Détection des risques</span>
          </div>
          <div className="feature">
            <svg className="feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            <span className="feature-text">Procédures BODACC</span>
          </div>
          <div className="feature">
            <svg className="feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
            </svg>
            <span className="feature-text">Données financières</span>
          </div>
        </div>
        </div>
      )}

      {showResults && (
        <div className="home-results">
          <div className="search-bar-top">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="SIRET (14 chiffres) ou nom d'entreprise"
              className="search-input-top"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch(e);
                }
              }}
            />
            <div
              style={{ position: 'relative', display: 'inline-block' }}
              onMouseEnter={() => {
                if (tooltipTimeout) clearTimeout(tooltipTimeout);
                if (isAuthenticated && quotaLeft <= 0) {
                  setShowQuotaTooltip(true);
                }
              }}
              onMouseLeave={() => {
                const timeout = setTimeout(() => {
                  setShowQuotaTooltip(false);
                }, 300);
                setTooltipTimeout(timeout);
              }}
            >
              <button
                onClick={handleSearch}
                className="search-btn-top"
                disabled={loading || (isAuthenticated && quotaLeft <= 0)}
              >
                {loading ? 'Recherche...' : 'Rechercher'}
              </button>
              {showQuotaTooltip && isAuthenticated && quotaLeft <= 0 && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 'calc(100% + 10px)',
                    right: 0,
                    background: 'white',
                    border: '1px solid #000',
                    padding: '16px',
                    minWidth: '320px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000
                  }}
                  onMouseEnter={() => {
                    if (tooltipTimeout) clearTimeout(tooltipTimeout);
                    setShowQuotaTooltip(true);
                  }}
                  onMouseLeave={() => {
                    const timeout = setTimeout(() => {
                      setShowQuotaTooltip(false);
                    }, 300);
                    setTooltipTimeout(timeout);
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: '2px solid #000',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      flexShrink: 0
                    }}>i</div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '0.9rem', color: '#000', marginBottom: '12px', fontWeight: '500' }}>
                        Quota de vérifications épuisé
                      </p>
                      <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '12px' }}>
                        Passez à une offre supérieure ou contactez-nous pour une offre sur mesure.
                      </p>
                      <a href="mailto:contact@b2bcheck.fr" style={{
                        display: 'inline-block',
                        padding: '8px 16px',
                        background: '#000',
                        color: '#fff',
                        textDecoration: 'none',
                        fontSize: '0.85rem',
                        borderRadius: '2px'
                      }}>
                        Nous contacter
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <h2>Sélectionnez une entreprise</h2>
          <p className="results-count">{searchResults.length} résultat(s) trouvé(s)</p>

          {searchResults.length === 0 ? (
            <div className="no-results">
              <p>Aucune vérification effectuée pour le moment</p>
            </div>
          ) : (
            <>
              <div className="results-grid">
                {searchResults.map((company, index) => (
                <div
                  key={index}
                  className="result-card"
                  onClick={() => {
                    onViewReport('history');
                    setTimeout(() => startScan(company.siret, company.nom), 100);
                    setShowResults(false);
                    setQuery('');
                  }}
                >
                  <div className="result-header">
                    <h3>{company.nom}</h3>
                    <span className={`status-badge ${company.statut === 'active' ? 'active' : 'inactive'}`}>
                      {company.statut}
                    </span>
                  </div>

                  <div className="result-info">
                    <div className="info-row">
                      <span className="label">SIRET:</span>
                      <span className="value">{company.siret}</span>
                    </div>
                    {company.forme_juridique && (
                      <div className="info-row">
                        <span className="label">Forme:</span>
                        <span className="value">{company.forme_juridique}</span>
                      </div>
                    )}
                    {company.activite && (
                      <div className="info-row">
                        <span className="label">Activité:</span>
                        <span className="value">{company.activite}</span>
                      </div>
                    )}
                    {company.lieu && (
                      <div className="info-row">
                        <span className="label">Lieu:</span>
                        <span className="value">{company.lieu} ({company.code_postal})</span>
                      </div>
                    )}
                  </div>

                  <div className="result-action">
                    Voir le rapport →
                  </div>
                </div>
                ))}

                {loading && Array.from({ length: getSkeletonCount() }).map((_, i) => (
                  <div key={`skeleton-${i}`} className="skeleton-card">
                    <div className="skeleton-header">
                      <div className="skeleton-title"></div>
                      <div className="skeleton-badge"></div>
                    </div>
                    <div className="skeleton-info">
                      <div className="skeleton-line"></div>
                      <div className="skeleton-line medium"></div>
                      <div className="skeleton-line short"></div>
                      <div className="skeleton-line"></div>
                    </div>
                    <div className="skeleton-button"></div>
                  </div>
                ))}
              </div>

              {currentPage < totalPages && (
                <div className="load-more-section">
                  <button onClick={loadMoreResults} className="btn-load-more" disabled={loading}>
                    {loading ? 'Chargement...' : `Charger plus (${currentPage}/${totalPages})`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default Home;
